import React, {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
} from "react";
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
} from "@chakra-ui/react";

export interface ConfirmOptions {
  /** Short heading, e.g. "Delete 3 items?" */
  title?: string;
  /** What will happen. Line breaks ("\n") are kept. */
  message: ReactNode;
  /** Defaults to "Delete". */
  confirmLabel?: string;
  /** Defaults to "Cancel". */
  cancelLabel?: string;
  /** "danger" = red confirm button (deletes), "primary" = blue (e.g. activate). Defaults to "danger". */
  tone?: "danger" | "primary";
}

type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

/**
 * One shared "are you sure?" dialog for every delete-like action in the app,
 * replacing scattered window.confirm calls so they all look and read the same
 * and can say what will actually happen.
 *
 *   const confirm = useConfirm();
 *   if (!(await confirm({ title: "Delete folder?", message: "..." }))) return;
 *
 * The dialog closes as soon as an answer is chosen; the caller then runs its
 * own request (and shows its own loading state). Cancel has the initial focus,
 * so pressing Enter right after the dialog opens never deletes by accident.
 */
export const ConfirmDialogProvider = ({ children }: { children: ReactNode }) => {
  const [options, setOptions] = useState<ConfirmOptions | null>(null);
  const resolverRef = useRef<((answer: boolean) => void) | null>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);

  const confirm = useCallback<ConfirmFn>((opts) => {
    return new Promise<boolean>((resolve) => {
      // A new request while one is still open counts as "no" for the old one.
      resolverRef.current?.(false);
      resolverRef.current = resolve;
      setOptions(opts);
    });
  }, []);

  const answer = (result: boolean) => {
    resolverRef.current?.(result);
    resolverRef.current = null;
    setOptions(null);
  };

  const tone = options?.tone ?? "danger";

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <AlertDialog
        isOpen={options !== null}
        leastDestructiveRef={cancelRef}
        onClose={() => answer(false)}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              {options?.title ?? "Are you sure?"}
            </AlertDialogHeader>
            <AlertDialogBody whiteSpace="pre-line">{options?.message}</AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={() => answer(false)}>
                {options?.cancelLabel ?? "Cancel"}
              </Button>
              <Button
                colorScheme={tone === "danger" ? "red" : "blue"}
                onClick={() => answer(true)}
                ml={3}
              >
                {options?.confirmLabel ?? "Delete"}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </ConfirmContext.Provider>
  );
};

export const useConfirm = (): ConfirmFn => {
  const confirm = useContext(ConfirmContext);
  if (!confirm) {
    throw new Error("useConfirm must be used inside <ConfirmDialogProvider>");
  }
  return confirm;
};
